import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { useTitle } from "../../../hooks/useTitle";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import AppPage from "../../../components/CustomUI/AppPage";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import CardContainer from "../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import Button from "../../../components/BaseUI/Button";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import Lozenge from "../../../components/BaseUI/Lozenge";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import SidebarContainer from "../../../components/CustomUI/SidebarContainer";
import Input from "../../../components/BaseUI/Input";
import Label from "../../../components/BaseUI/Label";
import ButtonGroup from "../../../components/BaseUI/ButtonGroup";
import { FiPlus, FiEdit2, FiTrash2, FiCheck } from "react-icons/fi";
import { withAuth } from "../../../components/HOC/withAuth";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const PRESET_COLORS = [
  "#ef4444", "#f97316", "#eab308", "#22c55e",
  "#3b82f6", "#6366f1", "#8b5cf6", "#ec4899",
  "#14b8a6", "#64748b",
];

const tableColumns = [
  { name: "", id: 0, sortable: false },
  { name: "Ad", dataType: "text", id: 1, customSorter: (a, b) => String(a?.name || "").localeCompare(String(b?.name || "")) },
  { name: "OEE Sınıfı", dataType: "text", id: 2, customSorter: (a, b) => String(a?.oeeClass?.name || "").localeCompare(String(b?.oeeClass?.name || "")) },
  { name: "Açıklama", id: 3, sortable: false },
  { name: "Durum", dataType: "text", id: 4, customSorter: (a, b) => Number(!!b?.isActive) - Number(!!a?.isActive) },
  { name: "İşlemler", id: 5, sortable: false },
];

const emptyForm = () => ({ name: "", description: "", color: "#6366f1", oeeClass: "", isActive: true });

const OEEGroups = () => {
  useTitle("Rovion MES - OEE Grupları");
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(currentUser.role, "/oee-groups", currentUser.pages || [], !!currentUser.fullAccess);

  const [groups, setGroups] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sidebarOpened, setSidebarOpened] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, id: null });

  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("oee-groups-list", tableColumns);

  const dismissToast = useCallback((id) => setToasts((p) => p.filter((t) => t.id !== id)), []);
  const addToast = useCallback((message, type = "success") => {
    setToasts((p) => [...p, { id: Date.now(), message, type }]);
  }, []);
  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken]
  );

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/oee-groups`, authHeaders());
      setGroups(data.oeeGroups || []);
    } catch {
      addToast("OEE Grupları yüklenemedi", "danger");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, addToast]);

  const fetchClasses = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/oee-classes`, authHeaders());
      setClasses(data.oeeClasses || []);
    } catch { }
  }, [authHeaders]);

  useEffect(() => { fetchGroups(); fetchClasses(); }, [fetchGroups, fetchClasses]);

  const openCreate = () => { setFormData(emptyForm()); setEditingItem(null); setSidebarOpened(true); };
  const openEdit = (item) => {
    setFormData({
      name: item.name || "",
      description: item.description || "",
      color: item.color || "#6366f1",
      oeeClass: item.oeeClass?._id || item.oeeClass || "",
      isActive: item.isActive !== false,
    });
    setEditingItem(item);
    setSidebarOpened(true);
  };
  const closeSidebar = () => { setSidebarOpened(false); setEditingItem(null); setFormData(emptyForm()); };

  const handleSave = async () => {
    if (!formData.name.trim()) { addToast("Ad zorunludur", "danger"); return; }
    if (!formData.oeeClass) { addToast("OEE Sınıfı seçiniz", "danger"); return; }
    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        color: formData.color || "#6366f1",
        oeeClass: formData.oeeClass,
        isActive: formData.isActive,
      };
      if (editingItem) {
        await axios.put(`${API_URL}/api/oee-groups/${editingItem._id}`, payload, authHeaders());
        addToast("Güncellendi", "success");
      } else {
        await axios.post(`${API_URL}/api/oee-groups`, payload, authHeaders());
        addToast("Oluşturuldu", "success");
      }
      closeSidebar();
      fetchGroups();
    } catch (err) {
      addToast(err?.response?.data?.message || "Kayıt başarısız", "danger");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/oee-groups/${id}`, authHeaders());
      addToast("Silindi", "success");
      fetchGroups();
    } catch (err) {
      addToast(err?.response?.data?.message || "Silme başarısız", "danger");
    } finally {
      setConfirmModal({ isOpen: false, id: null });
    }
  };

  const selectStyle = { background: "var(--m-card-background-color, #fff)", border: "1px solid #e2e5e8", borderRadius: "6px" };
  const selectInner = { background: "transparent", border: "none", outline: "none", width: "100%", padding: "8px 12px", fontSize: "14px", color: "inherit", cursor: "pointer" };

  return (
    <AppPage
      title="OEE Grupları"
      actions={canWrite && <Button appearance="primary-animated" onClick={openCreate} icon={<FiPlus />}>Yeni Grup</Button>}
    >
      <ToastContainer>
        {toasts.map((t) => (
          <Toast key={t.id} title={t.message} appearance={t.type === "danger" ? "danger" : "success"} onClose={() => dismissToast(t.id)} />
        ))}
      </ToastContainer>

      <Modal isOpen={confirmModal.isOpen} onClose={() => setConfirmModal({ isOpen: false, id: null })} width="small">
        <ModalHeader>OEE Grubunu Sil</ModalHeader>
        <ModalBody><p className="sub-title-page">Bu grubu silmek istediğinizden emin misiniz?</p></ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button appearance="danger" onClick={() => handleDelete(confirmModal.id)}>Sil</Button>
            <Button appearance="subtle" onClick={() => setConfirmModal({ isOpen: false, id: null })}>İptal</Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      <CardContainer>
        <Table
          columns={tableColumns}
          visibleColumns={visibleColumns}
          loading={loading}
          search={true}
          viewOptions={
            <ListViewOptionsPanel
              columns={tableColumns}
              visibleColumns={visibleColumns}
              onToggleColumn={toggleColumnVisibility}
              onResetColumns={resetToDefault}
              showApply={false}
              showReset={false}
            />
          }
        >
          {groups.map((g) => {
            const cls = g.oeeClass;
            return (
              <Tr key={g._id} data={g}>
                {visibleColumns.includes(0) && (
                  <Td>
                    <span style={{ display: "inline-block", width: 12, height: 12, borderRadius: "50%", background: g.color || "#6366f1", border: "1px solid rgba(0,0,0,0.1)", flexShrink: 0 }} />
                  </Td>
                )}
                {visibleColumns.includes(1) && (
                  <Td><strong style={{ fontSize: "0.875rem" }}>{g.name}</strong></Td>
                )}
                {visibleColumns.includes(2) && (
                  <Td>
                    {cls ? (
                      <div className="d-flex align-items-center gap-2">
                        <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: cls.color || "#6366f1", flexShrink: 0 }} />
                        <span style={{ fontSize: "0.82rem", fontWeight: 600 }}>{cls.name}</span>
                      </div>
                    ) : <span className="text-muted small">—</span>}
                  </Td>
                )}
                {visibleColumns.includes(3) && (
                  <Td><span className="text-muted small">{g.description || "—"}</span></Td>
                )}
                {visibleColumns.includes(4) && (
                  <Td>
                    {g.isActive ? <Lozenge appearance="success-subtle" isBold>Aktif</Lozenge> : <Lozenge appearance="default-subtle" isBold>Pasif</Lozenge>}
                  </Td>
                )}
                {visibleColumns.includes(5) && (
                  <Td>
                    {canWrite && (
                      <div className="d-flex gap-2">
                        <IconButton icon={<FiEdit2 />} label="Düzenle" appearance="subtle" shape="circle" onClick={() => openEdit(g)} />
                        <IconButton icon={<FiTrash2 />} label="Sil" appearance="subtle" shape="circle" onClick={() => setConfirmModal({ isOpen: true, id: g._id })} />
                      </div>
                    )}
                  </Td>
                )}
              </Tr>
            );
          })}
        </Table>
      </CardContainer>

      <SidebarContainer show={sidebarOpened} title={editingItem ? "OEE Grubu Düzenle" : "Yeni OEE Grubu"} onClose={closeSidebar}>
        <div className="d-flex flex-column" style={{ height: "100%" }}>
          <div className="d-flex flex-column gap-3">
            <div>
              <Label required>Ad</Label>
              <Input value={formData.name} onChange={(v) => setFormData((p) => ({ ...p, name: v }))} placeholder="örn. Makine Arızası" />
            </div>

            <div>
              <Label required>OEE Sınıfı</Label>
              <div style={selectStyle}>
                <select style={selectInner} value={formData.oeeClass} onChange={(e) => setFormData((p) => ({ ...p, oeeClass: e.target.value }))}>
                  <option value="">— Sınıf Seçin —</option>
                  {classes.map((c) => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label>Açıklama</Label>
              <Input value={formData.description} onChange={(v) => setFormData((p) => ({ ...p, description: v }))} placeholder="Kısa açıklama (isteğe bağlı)" />
            </div>

            <div>
              <Label>Renk</Label>
              <div className="d-flex flex-wrap gap-2 mt-1">
                {PRESET_COLORS.map((c) => (
                  <button key={c} type="button" onClick={() => setFormData((p) => ({ ...p, color: c }))}
                    style={{ width: 28, height: 28, borderRadius: "50%", background: c, border: "2px solid rgba(0,0,0,0.08)", cursor: "pointer", outline: "none", boxShadow: formData.color === c ? "0 0 0 3px #111827" : "none", transition: "box-shadow 0.15s", flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff" }}>
                    {formData.color === c && <FiCheck size={14} style={{ filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.35))" }} />}
                  </button>
                ))}
                <label style={{ width: 28, height: 28, borderRadius: "50%", border: "2px dashed #cbd5e1", background: !PRESET_COLORS.includes(formData.color) && formData.color ? formData.color : "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", position: "relative", overflow: "hidden", fontSize: 16, color: !PRESET_COLORS.includes(formData.color) && formData.color ? "#fff" : "#64748b", flexShrink: 0, boxShadow: !PRESET_COLORS.includes(formData.color) && formData.color ? "0 0 0 3px #111827" : "none" }}>
                  <input type="color" value={formData.color} onChange={(e) => setFormData((p) => ({ ...p, color: e.target.value }))} style={{ position: "absolute", width: "200%", height: "200%", top: "-50%", left: "-50%", opacity: 0, cursor: "pointer" }} />
                  {!PRESET_COLORS.includes(formData.color) ? <FiCheck size={14} /> : "+"}
                </label>
              </div>
            </div>
          </div>

          <div className="mt-auto pt-3">
            <Button appearance="primary-animated" onClick={handleSave} disabled={saving} icon={editingItem ? <FiEdit2 /> : <FiPlus />} className="w-100">
              {saving ? "Kaydediliyor..." : editingItem ? "Güncelle" : "Oluştur"}
            </Button>
          </div>
        </div>
      </SidebarContainer>
    </AppPage>
  );
};

export default withAuth(OEEGroups);
